import { FAIcon } from '@/components/fa-icon'
import { readableTextColor } from '@/components/tag-color-utils'
import { cn } from '@/lib/utils'
import type { TagNode } from '@/modules/common/types'

type Variant = 'chip' | 'pill' | 'dot'

interface Props {
  tag: Pick<TagNode, 'id' | 'name' | 'color'>
  variant?: Variant
  onRemove?: () => void
  className?: string
}

/**
 * Coloured tag indicator. Variants:
 *
 * - ``chip``: editable picker chip with optional remove icon. Square corners.
 * - ``pill``: read-only fully-rounded badge for displaying tags on issues.
 * - ``dot``: small color dot + tag name; used inside dropdowns/combobox rows.
 */
export const TagBadge: React.FC<Props> = ({
  tag,
  variant = 'chip',
  onRemove,
  className,
}) => {
  if (variant === 'dot') {
    return (
      <span className={cn('inline-flex items-center gap-2', className)}>
        <span
          className="inline-block size-3 rounded-full shrink-0"
          style={{ backgroundColor: tag.color }}
        />
        {tag.name}
      </span>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 text-xs cursor-default',
        variant === 'chip' && 'rounded-md',
        variant === 'pill' && 'rounded-full font-medium',
        className
      )}
      style={{
        backgroundColor: tag.color,
        color: readableTextColor(tag.color),
      }}
      onClick={evt => evt.stopPropagation()}
    >
      {tag.name}
      {onRemove && (
        <FAIcon
          className="ml-1 cursor-pointer opacity-80 hover:opacity-100"
          icon="times"
          onClick={onRemove}
        />
      )}
    </span>
  )
}
